import { Async, toAsyncFetched } from '@client/new/helpers/types';
import {
  ApolloCache,
  NormalizedCacheObject,
  gql,
  useQuery,
} from '@apollo/client';
import {
  ChecklistScope,
  TaskCompletionMode,
  useMarkChecklistAsCompletedMutation,
  useSaveOnboardingTaskProgressMutation,
  useSetActiveOnboardingChecklistMutation,
  useSetCreatorAsLiveMutation,
} from 'generated/graphql';
import { useInfluencer } from '@client/new/common/LiteUser.mapper';
import { mapChecklists, mapStrategy } from './Guide.mappers';
import { Checklist, GuideModel, ToggleTaskInput } from './Guide.types';
import { filterChecklistsForStrategy } from './checklistFiltering';

const GET_ONBOARDING_GUIDE_DATA = gql`
  query GetOnboardingGuideData($creatorId: ID!, $creatorIdInt: Int!) {
    GetChecklists(creatorId: $creatorId) {
      id
      title
      description
      estimatedDurationLabel
      scope
      strategyIds
      isActive
      tasks {
        id
        title
        subtitle
        actionUrl
        actionModal
        actionPage
        estimatedTimeLabel
        completionMode
        isChecked
        completedAt
        completedBy
      }
    }
    GetStrategies(creatorId: $creatorId) {
      id
      name
      isActive
      isSelected
      launchTimeLabel
    }
    onboardingListData(creatorId: $creatorIdInt) {
      profileReady
      paymentMethodReady
      collectionPublishedReady
    }
  }
`;

type GuideChecklistTask = {
  id: string;
  title: string;
  subtitle?: string | null;
  actionUrl?: string | null;
  actionModal?: string | null;
  actionPage?: string | null;
  estimatedTimeLabel?: string | null;
  completionMode: TaskCompletionMode;
  isChecked: boolean;
  completedAt?: string | null;
  completedBy?: string | null;
};

type GuideChecklist = {
  id: string;
  title: string;
  description?: string | null;
  estimatedDurationLabel?: string | null;
  scope: ChecklistScope;
  strategyIds: string[];
  isActive: boolean;
  tasks: GuideChecklistTask[];
};

type GuideStrategy = {
  id: string;
  name: string;
  isActive: boolean;
  isSelected?: boolean | null;
  launchTimeLabel?: string | null;
};

type GuideOnboardingListData = {
  profileReady: boolean;
  paymentMethodReady: boolean;
  collectionPublishedReady: boolean;
} | null;

type GetOnboardingGuideData = {
  GetChecklists: GuideChecklist[];
  GetStrategies: GuideStrategy[];
  onboardingListData?: GuideOnboardingListData;
};

type GetOnboardingGuideDataVariables = {
  creatorId: string;
  creatorIdInt: number;
};

const updateTaskCache = (
  cache: ApolloCache<NormalizedCacheObject>,
  taskId: string,
  completed: boolean,
  creatorId: string,
  creatorIdInt: number,
): void => {
  cache.updateQuery<GetOnboardingGuideData, GetOnboardingGuideDataVariables>(
    {
      query: GET_ONBOARDING_GUIDE_DATA,
      variables: {
        creatorId,
        creatorIdInt,
      },
    },
    (data) => {
      if (!data) return data;

      return {
        ...data,
        GetChecklists: data.GetChecklists.map((checklist) => ({
          ...checklist,
          tasks: checklist.tasks.map((task) => {
            if (task.id !== taskId) return task;

            return {
              ...task,
              isChecked: completed,
              completedAt: completed
                ? task.completedAt ?? new Date().toISOString()
                : null,
              completedBy: completed ? task.completedBy ?? null : null,
            };
          }),
        })),
      };
    },
  );
};

export const useGuideModel = (
  influencerId: number,
  selectedStrategyId: string | null,
): Async<GuideModel> => {
  const creatorId = String(influencerId);

  const influencerAsync = useInfluencer(influencerId);
  const isLive =
    influencerAsync.type === 'fetched' ? influencerAsync.value.isLive : false;

  const { data, loading, error, refetch } = useQuery<
    GetOnboardingGuideData,
    GetOnboardingGuideDataVariables
  >(GET_ONBOARDING_GUIDE_DATA, {
    variables: {
      creatorId,
      creatorIdInt: influencerId,
    },
    skip: !influencerId,
    fetchPolicy: 'network-only',
  });

  const [saveTaskProgressMutation] = useSaveOnboardingTaskProgressMutation();

  const [setActiveChecklistMutation] =
    useSetActiveOnboardingChecklistMutation();

  const [setCreatorAsLiveMutation, setCreatorAsLiveState] =
    useSetCreatorAsLiveMutation({
      variables: {
        creatorId: influencerId,
      },
    });

  const [markChecklistAsCompletedMutation] =
    useMarkChecklistAsCompletedMutation();

  const toggleTask = async (input: ToggleTaskInput): Promise<void> => {
    await saveTaskProgressMutation({
      variables: { creatorId, input },
      optimisticResponse: {
        __typename: 'Mutation',
        SaveTaskProgress: true,
      },
      update: (cache) => {
        updateTaskCache(
          cache,
          input.taskId,
          input.completed,
          creatorId,
          influencerId,
        );
      },
    });
    await refetch();
  };

  const setActiveChecklist = async (checklistId: string): Promise<void> => {
    await setActiveChecklistMutation({
      variables: { creatorId, input: { checklistId } },
    });
    await refetch();
  };

  const goLive = async (): Promise<void> => {
    await setCreatorAsLiveMutation();
  };

  const markChecklistCompleted = async (checklistId: string): Promise<void> => {
    await markChecklistAsCompletedMutation({
      variables: { creatorId, checklistId },
    });
    await refetch();
  };

  // Query is skipped while waiting for influencerId
  if (!influencerId) return { type: 'fetching' };
  if (loading) return { type: 'fetching' };
  if (error) return { type: 'fetch-error', value: error.message };
  if (!data) return { type: 'fetch-error', value: 'No data returned' };

  const checklists = mapChecklists(data.GetChecklists);
  const hasStrategyScopedChecklists = checklists.some(
    (c) => c.scope === ChecklistScope.Strategy,
  );

  const strategyIdForFilter =
    selectedStrategyId ??
    data.GetStrategies.find((s) => s.isSelected)?.id ??
    null;

  const filteredChecklists = hasStrategyScopedChecklists
    ? filterChecklistsForStrategy(checklists, strategyIdForFilter)
    : checklists;

  const strategy = mapStrategy(data.GetStrategies, strategyIdForFilter);

  return toAsyncFetched({
    checklists: filteredChecklists,
    strategy,
    isLive,
    influencerId,
    goLiveInProgress: setCreatorAsLiveState.loading,
    onboardingStatus: {
      profileReady: data.onboardingListData?.profileReady ?? false,
      paymentMethodReady: data.onboardingListData?.paymentMethodReady ?? false,
      collectionPublishedReady:
        data.onboardingListData?.collectionPublishedReady ?? false,
    },
    toggleTask,
    setActiveChecklist,
    markChecklistCompleted,
    goLive,
  });
};
